import type { BatchPersonRawRow, ParseBatchFileResult } from "./types";
import {
  FORMAT_ERROR,
  isAllowedBatchPersonFile,
} from "./batchPersonFileAccept";
import { resolveColumnMapping, rowFromCells } from "./resolveColumnMapping";

function splitCsvLine(line: string, delimiter: string): string[] {
  const result: string[] = [];
  let current = "";
  let inQuotes = false;
  for (let i = 0; i < line.length; i += 1) {
    const char = line[i];
    if (char === '"') {
      if (inQuotes && line[i + 1] === '"') {
        current += '"';
        i += 1;
      } else {
        inQuotes = !inQuotes;
      }
    } else if (char === delimiter && !inQuotes) {
      result.push(current.trim());
      current = "";
    } else {
      current += char;
    }
  }
  result.push(current.trim());
  return result;
}

function detectDelimiter(headerLine: string): string {
  const commas = (headerLine.match(/,/g) || []).length;
  const semis = (headerLine.match(/;/g) || []).length;
  return semis > commas ? ";" : ",";
}

function rowsFromRecords(
  records: Array<Record<string, unknown>>,
): ParseBatchFileResult {
  if (records.length === 0) {
    return { ok: false, message: "O arquivo está vazio." };
  }

  const headers = Object.keys(records[0] || {});
  const sampleRows = records.slice(0, 10).map((record) =>
    headers.map((key) => (record[key] == null ? "" : String(record[key]))),
  );
  const mapping = resolveColumnMapping(headers, sampleRows);
  if (!mapping) {
    return { ok: false, message: FORMAT_ERROR };
  }

  const rows: BatchPersonRawRow[] = records.map((record, index) => {
    const cells = headers.map((key) =>
      record[key] == null ? "" : String(record[key]),
    );
    return rowFromCells(cells, mapping.indexes, index + 1);
  });

  return { ok: true, rows, usedFlexibleMapping: mapping.usedFlexibleMapping };
}

function parseCsvText(text: string): ParseBatchFileResult {
  const lines = text
    .replace(/^\uFEFF/, "")
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter((line) => line.length > 0);

  if (lines.length < 2) {
    return { ok: false, message: FORMAT_ERROR };
  }

  const delimiter = detectDelimiter(lines[0]);
  const headers = splitCsvLine(lines[0], delimiter);
  const dataLines = lines.slice(1).map((line) => splitCsvLine(line, delimiter));
  const mapping = resolveColumnMapping(headers, dataLines.slice(0, 10));
  if (!mapping) {
    return { ok: false, message: FORMAT_ERROR };
  }

  const rows: BatchPersonRawRow[] = dataLines.map((cells, index) =>
    rowFromCells(cells, mapping.indexes, index + 2),
  );

  return { ok: true, rows, usedFlexibleMapping: mapping.usedFlexibleMapping };
}

function parseJsonText(text: string): ParseBatchFileResult {
  let parsed: unknown;
  try {
    parsed = JSON.parse(text);
  } catch {
    return { ok: false, message: FORMAT_ERROR };
  }
  if (!Array.isArray(parsed)) {
    return { ok: false, message: FORMAT_ERROR };
  }
  const records = parsed.filter(
    (item): item is Record<string, unknown> =>
      Boolean(item) && typeof item === "object" && !Array.isArray(item),
  );
  if (records.length !== parsed.length) {
    return { ok: false, message: FORMAT_ERROR };
  }
  return rowsFromRecords(records);
}

export function parseBatchPersonFile(
  file: File,
  text: string,
): ParseBatchFileResult {
  if (!isAllowedBatchPersonFile(file)) {
    return { ok: false, message: FORMAT_ERROR };
  }
  const name = file.name.toLowerCase();
  if (name.endsWith(".json") || file.type === "application/json") {
    return parseJsonText(text);
  }
  return parseCsvText(text);
}

export { FORMAT_ERROR, isAllowedBatchPersonFile };
export { BATCH_PERSON_CSV_TEMPLATE } from "./batchPersonFileAccept";
